import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

/**
 * Keyboard shortcuts for power-users in the merchant dashboard.
 * Active only when the URL starts with /dashboard.
 *
 *  S  → Scanner
 *  R  → Reservations (Buchungen)
 *  T  → Tische (Floor Plan)
 *  L  → Loyalty
 *  P  → Pickup
 *  H  → Quick Hub
 *  D  → Dashboard Home
 *  ?  → Help overlay (toast list)
 */
const SHORTCUTS: { key: string; to: string; label: string }[] = [
  { key: "s", to: "/dashboard/scanner", label: "Scanner" },
  { key: "r", to: "/dashboard/messages", label: "Buchungen" },
  { key: "t", to: "/dashboard/floorplan", label: "Tische" },
  { key: "l", to: "/dashboard/loyalty", label: "Loyalty" },
  { key: "p", to: "/dashboard/pickup", label: "Abholungen" },
  { key: "h", to: "/dashboard/hub", label: "Quick Hub" },
  { key: "d", to: "/dashboard", label: "Übersicht" },
];

const MerchantKeyboardShortcuts = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!location.pathname.startsWith("/dashboard")) return;

    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName;
      const isEditable =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target.isContentEditable;
      if (isEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === "?") {
        e.preventDefault();
        toast("⌨️ Tastenkürzel", {
          description: SHORTCUTS.map((s) => `${s.key.toUpperCase()} – ${s.label}`).join(" · "),
          duration: 6000,
        });
        return;
      }

      const match = SHORTCUTS.find((s) => s.key === e.key.toLowerCase());
      if (match) {
        e.preventDefault();
        navigate(match.to);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate, location.pathname]);

  return null;
};

export default MerchantKeyboardShortcuts;
